import { STATIC_PATH_FALLBACK } from 'constants/environment';
import Hero from 'components/Pages/Blog/Hero';
import { PostItem, Tags } from 'components/Pages/Blog';
import GetInTouch from 'components/Pages/GetInTouch';
import ConversionBar from 'components/Shared/ConversionBar';
import Pagination from 'components/Shared/Pagination';
import Layout from 'components/ui/Layout';
import config from 'config';
import { createGraphqlClient } from 'data/graphql/utils/createGraphqlClient';
import GET_BLOG_ITEMS from 'data/graphql/queries/blog';
import { getAuthorsBaseSlug } from 'data/utils/author';
import { getMainFooter, skipServerSideBuild } from 'data/page/utils';
import { getBrand } from 'data/page/utils/getBrand';
import { getPopularTagsFromPosts } from 'utils/getPopularTagsFromPosts';
import { getContentfulLocale } from 'utils/locale';
import { useTranslation } from 'hooks/useTranslation';
import { contentfulClient, contentfulClientPreview } from 'services/contentful';
import type { AlternateSlug } from 'types/AlternateSlugs';
import type { BlogItem, BlogItemCollection, BlogTag } from 'types/Blog';
import type { Brand } from 'types/Brand';
import type { ITagFields } from 'types/contentful/generated';
import type { FooterItemType } from 'types/Footer';
import type { ConfigLocale } from 'types/PageData';
import type { GetStaticPaths, GetStaticProps } from 'next';
import type { EntryCollection } from 'contentful';

type Props = {
  items: BlogItem[];
  totalPages: number;
  tags: BlogTag[];
  alternateSlugs: AlternateSlug[];
  footer: FooterItemType | null;
  authorBaseSlug?: string | null;
  brand: Brand | null;
};

const Blog = ({
  items,
  totalPages,
  tags = [],
  alternateSlugs,
  footer,
  authorBaseSlug,
  brand,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { newPostsCount } = config.defaults;

  return (
    <Layout
      title={`${t('blog.home.metaTitle')} | ${t('qunomedical')}`}
      metaDescription={t('blog.home.metaDescription')}
      alternateSlugs={alternateSlugs}
      footer={footer}
      noMetaTitle={t('blog')}
      brand={brand}
    >
      <Hero>
        <h1 className="uppercase text-tertiary text-3xl md:text-5xl pt-0">
          {t('qunomedical')} {t('blog')}
        </h1>
        <p className="text-white md:text-lg">{t('blog.home.subHeadline')}</p>
      </Hero>

      <div className="max-w-screen-xl mx-auto px-5 md:px-4 my-12 md:mt-0">
        <h2>{t('blog.home.mostRecentPosts')}</h2>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-16">
          {items
            ?.slice(0, newPostsCount)
            .map((item, i) => (
              <PostItem
                key={`postItem_${i}`}
                fields={item}
                authorBaseSlug={authorBaseSlug}
                priorityImage
              />
            ))}
        </div>

        <div className="mt-6 -mx-3">
          <Tags tags={tags} headline={t('blog.popularTags')} />
        </div>
      </div>
      <GetInTouch />
      {items?.length > newPostsCount && (
        <div className="max-w-screen-xl mx-auto px-5 md:px-4 my-12">
          <h2 className="pt-0">{t('blog.home.allPosts')}</h2>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-16">
            {items
              ?.slice(newPostsCount)
              .map((item, i) => (
                <PostItem
                  key={i}
                  fields={item}
                  authorBaseSlug={authorBaseSlug}
                />
              ))}
          </div>
          <Pagination base="blog" pages={totalPages} firstIsBase />
        </div>
      )}
      <ConversionBar />
    </Layout>
  );
};

// eslint-disable-next-line
// @ts-ignore
export const getStaticProps: GetStaticProps<
  Props,
  { locale: string }
> = async ({ params, preview = false }) => {
  if (!STATIC_PATH_FALLBACK) {
    return {
      notFound: true,
    };
  }

  let entries: BlogItemCollection | null = null;
  let gqEntries = null;
  let tags: EntryCollection<ITagFields> | null = null;

  const { newPostsCount, postsPerPage } = config.defaults;

  const { locale } = params || {};
  const contentfulLocale = getContentfulLocale(locale);

  const graphqlClient = createGraphqlClient(preview);
  gqEntries = await graphqlClient.request(GET_BLOG_ITEMS, {
    locale: contentfulLocale,
    preview: !!preview,
  });

  if (!preview) {
    tags = await contentfulClient.getEntries({
      content_type: 'tag',
      locale: contentfulLocale,
      include: 2,
    });
  } else {
    tags = await contentfulClientPreview.getEntries({
      content_type: 'tag',
      locale: contentfulLocale,
      include: 2,
    });
  }

  entries = gqEntries.blogPageCollection;
  const filteredEntries = entries?.items
    .filter((entry): entry is BlogItem => !entry.disableLocale)
    .sort((a, b) => {
      const blogDateA =
        a.publishDate || a.sys.firstPublishedAt || a.sys.publishedAt;
      const blogDateB =
        b.publishDate || b.sys.firstPublishedAt || b.sys.publishedAt;

      return Date.parse(blogDateB) - Date.parse(blogDateA);
    });

  const filteredTags = tags?.items.filter((tag) => !tag.fields.disableLocale);
  const popularTags = getPopularTagsFromPosts(filteredEntries, filteredTags);
  const totalPages = Math.ceil(
    // eslint-disable-next-line
    // @ts-ignore
    (filteredEntries.length - newPostsCount) / postsPerPage,
  );

  // -- Create alternate slugs manually to inject hreflang tags
  const alternateSlugs = config.locales.map((entry: ConfigLocale) => ({
    locale: entry.main,
    slug: `blog`,
  }));

  const brand = await getBrand(process.env.BRAND_ID || null, locale);

  return {
    props: {
      locale,
      items: filteredEntries?.slice(0, newPostsCount + postsPerPage) || null,
      totalPages: totalPages || 1,
      tags: popularTags || null,
      alternateSlugs,
      footer: getMainFooter(locale),
      authorBaseSlug: getAuthorsBaseSlug(locale),
      brand,
    },
    revalidate: config.defaults.secondsForPageRevalidation,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (skipServerSideBuild()) {
    return {
      paths: [],
      fallback: STATIC_PATH_FALLBACK,
    };
  }

  const paths: { params: { locale: string } }[] = [];

  config.locales.forEach((locale: ConfigLocale) => {
    paths.push({
      params: {
        locale: locale.main,
      },
    });
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

export default Blog;
